// Primitive di disegno, font Unicode, date e mappe per il report PDF
import { MAPBOX_TOKEN, MAP_STYLE } from '../lib/mapConfig';

export const FONT = 'LiberationSans';
const FONT_FILES = [['LiberationSans-Regular.ttf', 'normal'], ['LiberationSans-Bold.ttf', 'bold']];
const FONT_BASE = `${process.env.PUBLIC_URL || ''}/fonts`;
const TZ = 'Europe/Rome';

export const C = {
  brand: '#4A7C59', brandDeep: '#2F5D43', tint: '#EFF4F0', ink: '#16241C', body: '#434E48', muted: '#8B958F',
  rule: '#E1E7E2', ruleLight: '#F0F3F1', cream: '#FAF9F6', farmaci: '#4A7C59', pasti: '#B9822C', attivita: '#2F8F86',
  passo: '#6E9C62', umore: '#3E8E6E', neutra: '#9AA6A0', sos: '#B8443B', nota: '#97A29C', white: '#FFFFFF',
};

export const PAGE_W = 210;
export const PAGE_H = 297;
export const PAD = 17;
export const CONTENT_W = PAGE_W - PAD * 2;
export const RUN_H = 15;
export const FOOT_H = 13;
export const BOTTOM = PAGE_H - FOOT_H - 6;

export const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

export const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
};

const fontCache = {};
export const loadFonts = async (doc) => {
  for (const [file, style] of FONT_FILES) {
    if (!fontCache[file]) {
      const response = await fetch(`${FONT_BASE}/${file}`);
      if (!response.ok) throw new Error(`Font non disponibile: ${file}`);
      fontCache[file] = toBase64(await response.arrayBuffer());
    }
    doc.addFileToVFS(file, fontCache[file]);
    doc.addFont(file, FONT, style);
  }
  doc.setFont(FONT, 'normal');
};

// Testo con font, dimensione (pt) e colore in un'unica chiamata
export const text = (doc, value, x, y, { size = 9, color = C.body, bold = false, align = 'left', maxWidth, charSpace } = {}) => {
  doc.setFont(FONT, bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  doc.setTextColor(...rgb(color));
  const opts = { align };
  if (maxWidth) opts.maxWidth = maxWidth;
  if (charSpace) opts.charSpace = charSpace;
  doc.text(value, x, y, opts);
};

export const lines = (doc, value, width, size, bold = false) => {
  doc.setFont(FONT, bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  return doc.splitTextToSize(value, width);
};

export const lineH = (size, factor = 1.35) => size * 0.3528 * factor;

export const fill = (doc, hex) => doc.setFillColor(...rgb(hex));
export const stroke = (doc, hex, width = 0.25) => { doc.setDrawColor(...rgb(hex)); doc.setLineWidth(width); };

export const withOpacity = (doc, opacity, draw) => {
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity }));
  draw();
  doc.restoreGraphicsState();
};

// Anello di avanzamento (frazione 0-1) disegnato a segmenti
export const ring = (doc, cx, cy, r, frac, color, track, width) => {
  stroke(doc, track, width);
  doc.circle(cx, cy, r, 'S');
  const f = Math.max(0, Math.min(1, frac));
  if (f <= 0) return;
  stroke(doc, color, width);
  doc.setLineCap('round');
  const n = Math.max(2, Math.ceil(f * 120));
  let prev = null;
  for (let i = 0; i <= n; i += 1) {
    const a = (-90 + 360 * f * (i / n)) * (Math.PI / 180);
    const pt = [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    if (prev) doc.line(prev[0], prev[1], pt[0], pt[1]);
    prev = pt;
  }
  doc.setLineCap('butt');
};

export const sectionHead = (doc, y, title, note) => {
  text(doc, title.toUpperCase(), PAD, y, { size: 9, color: C.brand, bold: true, charSpace: 0.45 });
  if (note) text(doc, note, PAGE_W - PAD, y, { size: 8, color: C.muted, align: 'right' });
  stroke(doc, C.brand, 0.42);
  doc.line(PAD, y + 2.5, PAGE_W - PAD, y + 2.5);
  return y + 2.5;
};

export const tag = (doc, label, x, y, color) => {
  doc.setFont(FONT, 'normal');
  doc.setFontSize(7);
  const w = doc.getTextWidth(label) + 4;
  fill(doc, color);
  doc.roundedRect(x, y, w, 4.2, 1.4, 1.4, 'F');
  text(doc, label, x + 2, y + 3, { size: 7, color: C.white, charSpace: 0.1 });
};

// ---------- date e ore (fuso Europe/Rome) ----------
const fmt = (options) => new Intl.DateTimeFormat('it-IT', { timeZone: TZ, ...options });
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export const time = (d) => fmt({ hour: '2-digit', minute: '2-digit' }).format(d);
export const dayLong = (d) => cap(fmt({ weekday: 'long', day: 'numeric', month: 'long' }).format(d));
export const weekdayName = (d) => cap(fmt({ weekday: 'long' }).format(d));
export const weekdayShort = (d) => fmt({ weekday: 'short' }).format(d).replace('.', '').toUpperCase();
export const dayNum = (d) => fmt({ day: 'numeric' }).format(d);
export const dayMonthYear = (d) => fmt({ day: 'numeric', month: 'long', year: 'numeric' }).format(d);
export const shortStamp = (d) => `${fmt({ day: 'numeric', month: 'short' }).format(d).replace('.', '')} · ${time(d)}`;
export const keyToDate = (key) => new Date(`${key}T12:00:00Z`);

export const periodLabel = (startKey, endKey) => {
  const s = keyToDate(startKey);
  const e = keyToDate(endKey);
  const sameMonth = fmt({ month: 'long', year: 'numeric' }).format(s) === fmt({ month: 'long', year: 'numeric' }).format(e);
  const sameYear = fmt({ year: 'numeric' }).format(s) === fmt({ year: 'numeric' }).format(e);
  if (sameMonth) return `${dayNum(s)} – ${dayMonthYear(e)}`;
  if (sameYear) return `${fmt({ day: 'numeric', month: 'long' }).format(s)} – ${dayMonthYear(e)}`;
  return `${dayMonthYear(s)} – ${dayMonthYear(e)}`;
};

export const minutesLabel = (m) => (m === 1 ? '1 minuto' : `${m} minuti`);

// ---------- mappa (Mapbox Static Images) ----------
const stylePath = () => MAP_STYLE.replace('mapbox://styles/', '');

export const staticMapUrl = (walk, width = 600, height = 180) => {
  const overlays = [];
  if (walk.zone.length >= 3) {
    overlays.push(`geojson(${encodeURIComponent(JSON.stringify({
      type: 'Feature',
      properties: { stroke: C.brand, 'stroke-width': 1.6, 'stroke-opacity': 0.9, fill: C.brand, 'fill-opacity': 0.17 },
      geometry: { type: 'Polygon', coordinates: [[...walk.zone, walk.zone[0]]] },
    }))})`);
  }
  walk.pins.forEach((p) => {
    const isEnd = p.label === 'Rientro';
    overlays.push(`pin-s-${isEnd ? 'r' : 'p'}+${(isEnd ? C.brandDeep : C.brand).slice(1)}(${p.lng.toFixed(6)},${p.lat.toFixed(6)})`);
  });
  const single = walk.zone.length < 3 && walk.pins.length === 1;
  const position = single ? `${walk.pins[0].lng.toFixed(6)},${walk.pins[0].lat.toFixed(6)},15` : 'auto';
  const padding = single ? '' : '&padding=50';
  return `https://api.mapbox.com/styles/v1/${stylePath()}/static/${overlays.join(',')}/${position}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}${padding}`;
};

export const fetchMapImage = async (walk) => {
  if (!walk.pins.length) return null;
  try {
    const response = await fetch(staticMapUrl(walk));
    if (!response.ok) return null;
    return `data:image/png;base64,${toBase64(await response.arrayBuffer())}`;
  } catch (err) {
    return null;
  }
};
